import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { AnyFilter } from '../../../../shared/organizationPackages/filters';

import type { PackageInventorySearchParams } from '../../../hooks/usePackageInventorySearchParams';
import SearchInput from '../../generic/Forms/SearchInput';
import PackageSortingDropdown from '../PackageSortingDropdown';

import styles from './PackageSearchAndSort.module.less';

/**
 * PackageInventorySearchAndSort powers the Package Index top bar:
 * searching packages by name and controlling the sorting order.
 */
type PackageNameSearchProps = {
  initialValue: string;
  setFiltersDebounced: (filters: AnyFilter) => void;
};

const PackageNameSearch: VoidFunctionComponent<PackageNameSearchProps> = ({ initialValue, setFiltersDebounced }) => {
  const [packageName, setPackageName] = useState(initialValue);
  const [lastInitialValue, setLastInitialValue] = useState(initialValue);

  /* Resync the local value when the URL changes underneath us (reset, back/forward, manual edit),
   * but not when the debounced URL write echoes back the trimmed copy of what is already typed. */
  if (initialValue !== lastInitialValue) {
    setLastInitialValue(initialValue);
    if (initialValue !== packageName.trim()) {
      setPackageName(initialValue);
    }
  }

  const onChange = (name: string) => {
    setPackageName(name);
    setFiltersDebounced({ packageName: name });
  };
  return <SearchInput
    label="package-search"
    size="small"
    placeholder="Search by package name"
    value={packageName}
    onChange={onChange}
    testId="package-inventory-searchbar"
  />;
};

const PackageInventorySearchAndSort: VoidFunctionComponent<{
  searchParams: PackageInventorySearchParams;
}> = ({ searchParams }) => {
  const { filters, setFiltersDebounced } = searchParams;
  const packageName = filters.packageName ?? '';
  return (
    <div className={styles.filters}>
      <PackageNameSearch
        initialValue={packageName}
        setFiltersDebounced={setFiltersDebounced}
      />
      <PackageSortingDropdown
        packageName={packageName}
        sort={searchParams.sort}
        setSort={searchParams.setSort}
      />
    </div>
  );
};

export default PackageInventorySearchAndSort;
